import { AuthorizationStatus } from './enums';
import { ownershipProven } from './finding-state';

/**
 * What a paid agency still owes before further work on a model (repeat
 * scans, filing). The first free scan before a plan does not use this list.
 */
export type AgencySetupStep = 'invite' | 'bio' | 'letter';

export const AGENCY_SETUP_STEPS: readonly AgencySetupStep[] = ['invite', 'bio', 'letter'];

export const AGENCY_SETUP_LABEL: Readonly<Record<AgencySetupStep, string>> = {
  invite: 'Creator invitation confirmed',
  bio: 'OnlyFans bio notice verified',
  letter: 'Authorization letter accepted',
};

/** Facts the checklist is read from — no account kind or plan here. */
export interface AgencySetupFacts {
  inviteState: 'not_invited' | 'pending' | 'confirmed';
  /** Bio notice last seen and not later lost. */
  bioOk: boolean;
  authorizationStatus: AuthorizationStatus;
}

export function agencySetupGaps(facts: AgencySetupFacts): AgencySetupStep[] {
  const gaps: AgencySetupStep[] = [];
  if (facts.inviteState !== 'confirmed') gaps.push('invite');
  if (!facts.bioOk) gaps.push('bio');
  if (!ownershipProven(facts.authorizationStatus)) gaps.push('letter');
  return gaps;
}

export function agencySetupComplete(facts: AgencySetupFacts): boolean {
  return agencySetupGaps(facts).length === 0;
}

/** One sentence for a ForbiddenException / Notice — what is still missing. */
export function agencySetupMessage(gaps: AgencySetupStep[]): string {
  if (gaps.length === 0) return '';
  const labels = gaps.map((step) => AGENCY_SETUP_LABEL[step]);
  if (labels.length === 1) {
    return `Finish setup for this creator before continuing: ${labels[0].toLowerCase()}.`;
  }
  const last = labels[labels.length - 1];
  const head = labels.slice(0, -1).join(', ');
  return `Finish setup for this creator before continuing: ${head.toLowerCase()}, and ${last.toLowerCase()}.`;
}

/**
 * Bio notice still counts: verified once, and the latest check did not move
 * past that instant (same rule as the ownership worker's "lost").
 */
export function ownershipBioOk(
  verifiedAt: Date | string | null | undefined,
  checkedAt: Date | string | null | undefined,
): boolean {
  if (!verifiedAt) return false;
  if (!checkedAt) return true;
  return new Date(checkedAt).getTime() <= new Date(verifiedAt).getTime();
}
